import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, KIND_LABEL, CATEGORY_LABEL } from "@/lib/format";
import { toast } from "sonner";

// Universal transaction dialog. Preset lets you seed sides/category.
// If `editing` (a transaction object) is provided, we PUT to /api/transactions/{id}.
export default function TransactionDialog({ open, onClose, onSaved, preset = {}, invoiceId = null, editing = null, title }) {
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [category, setCategory] = useState("receipt");
  const [fromKind, setFromKind] = useState("customer");
  const [fromId, setFromId] = useState("");
  const [toKind, setToKind] = useState("cash_register");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [linkedInvoiceId, setLinkedInvoiceId] = useState("");
  const [invoiceOptions, setInvoiceOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/customers"), api.get("/suppliers"), api.get("/employees"), api.get("/cash-registers"),
    ]).then(([c, s, e, k]) => {
      setCustomers(c.data); setSuppliers(s.data); setEmployees(e.data); setCashRegisters(k.data);
    });
    if (editing) {
      setCategory(editing.category || "receipt");
      setFromKind(editing.from_kind);
      setFromId(editing.from_id || "");
      setToKind(editing.to_kind);
      setToId(editing.to_id || "");
      setAmount(String(editing.amount));
      setCurrency(editing.currency || "TRY");
      setDescription(editing.description || "");
      setDate((editing.date || new Date().toISOString()).slice(0, 16));
      setLinkedInvoiceId(editing.invoice_id || "");
    } else {
      setCategory(preset.category || "receipt");
      setFromKind(preset.from_kind || "customer");
      setFromId(preset.from_id || "");
      setToKind(preset.to_kind || "cash_register");
      setToId(preset.to_id || "");
      setCurrency(preset.currency || "TRY");
      setAmount("");
      setDescription("");
      setDate(new Date().toISOString().slice(0, 16));
      setLinkedInvoiceId("");
    }
  }, [open, editing]);

  // Determine which entity (customer/supplier) is involved to fetch matching invoices
  const linkableEntity = (() => {
    if (fromKind === "customer" && fromId) return { kind: "customer", id: fromId };
    if (toKind === "customer" && toId) return { kind: "customer", id: toId };
    if (fromKind === "supplier" && fromId) return { kind: "supplier", id: fromId };
    if (toKind === "supplier" && toId) return { kind: "supplier", id: toId };
    return null;
  })();
  const linkableEntityKey = linkableEntity ? `${linkableEntity.kind}:${linkableEntity.id}` : "";
  const canLinkInvoice = !!linkableEntity && !invoiceId; // Not when TxDialog is already opened FROM an invoice page (invoiceId prop)

  useEffect(() => {
    if (!open || !canLinkInvoice) { setInvoiceOptions([]); return; }
    const endpoint = linkableEntity.kind === "customer" ? "/sales-invoices" : "/purchase-invoices";
    api.get(endpoint, { params: { entity_id: linkableEntity.id } })
      .then(r => setInvoiceOptions(r.data || []))
      .catch(() => setInvoiceOptions([]));
    // Reset selection if the entity changed away from previously linked invoice
    // (only when not editing to avoid clobbering the initial value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkableEntityKey, canLinkInvoice]);

  const getList = (k) => ({ customer: customers, supplier: suppliers, employee: employees, cash_register: cashRegisters }[k] || []);

  const save = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Geçerli tutar girin"); return; }
    if (fromKind !== "external" && !fromId) { toast.error("Gönderen seçin"); return; }
    if (toKind !== "external" && !toId) { toast.error("Alıcı seçin"); return; }
    setSaving(true);
    try {
      const now = new Date();
      const dateStr = date.length <= 16
        ? new Date(`${date}:${String(now.getSeconds()).padStart(2,"0")}`).toISOString()
        : new Date(date).toISOString();
      const body = {
        from_kind: fromKind, from_id: fromKind === "external" ? null : fromId,
        to_kind: toKind, to_id: toKind === "external" ? null : toId,
        amount: Number(amount), currency, description,
        date: dateStr, category,
        invoice_id: canLinkInvoice ? (linkedInvoiceId || null) : (editing?.invoice_id || null),
      };
      if (editing) {
        await api.put(`/transactions/${editing.id}`, body);
        toast.success("İşlem güncellendi");
      } else {
        const endpoint = preset.invoice_endpoint || "/transactions";
        await api.post(endpoint, body);
        toast.success("İşlem kaydedildi");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem kaydedilemedi");
    } finally { setSaving(false); }
  };

  const kinds = ["customer", "supplier", "employee", "cash_register", "external"];
  const dialogTitle = title || (editing ? "İşlem Düzenle" : "Yeni İşlem");
  const isInvoiceLocked = editing && (editing.category === "invoice_sale" || editing.category === "invoice_purchase");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tx-dialog">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription className="text-xs">
            {isInvoiceLocked ? "Bu hareket bir faturaya bağlıdır ve düzenlenemez. Faturayı düzenleyin." : "Cari hareket bilgilerini girin."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kategori</Label>
            <Select value={category} onValueChange={setCategory} disabled={isInvoiceLocked}>
              <SelectTrigger data-testid="tx-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["receipt","payment","virman","salary","bonus","loan","borrow_from_employee","repay_to_employee","manual_deposit","manual_withdraw"].map(c=>(
                  <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gönderen</Label>
              <Select value={fromKind} onValueChange={(v) => { setFromKind(v); setFromId(""); }} disabled={isInvoiceLocked}>
                <SelectTrigger data-testid="tx-from-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{kinds.map(k=><SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>)}</SelectContent>
              </Select>
              {fromKind !== "external" && (
                <Select value={fromId} onValueChange={setFromId} disabled={isInvoiceLocked}>
                  <SelectTrigger className="mt-2" data-testid="tx-from-id-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>{getList(fromKind).map(x=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Alıcı</Label>
              <Select value={toKind} onValueChange={(v) => { setToKind(v); setToId(""); }} disabled={isInvoiceLocked}>
                <SelectTrigger data-testid="tx-to-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{kinds.map(k=><SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>)}</SelectContent>
              </Select>
              {toKind !== "external" && (
                <Select value={toId} onValueChange={setToId} disabled={isInvoiceLocked}>
                  <SelectTrigger className="mt-2" data-testid="tx-to-id-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>{getList(toKind).map(x=><SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Tutar</Label>
              <Input data-testid="tx-amount-input" type="number" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} disabled={isInvoiceLocked} />
            </div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={currency} onValueChange={setCurrency} disabled={isInvoiceLocked}>
                <SelectTrigger data-testid="tx-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Tarih & Saat</Label><Input data-testid="tx-date-input" type="datetime-local" value={date} onChange={(e)=>setDate(e.target.value)} disabled={isInvoiceLocked} /></div>
          <div><Label>Açıklama</Label><Textarea data-testid="tx-description-input" value={description} onChange={(e)=>setDescription(e.target.value)} rows={2} disabled={isInvoiceLocked} /></div>
          {canLinkInvoice && !isInvoiceLocked && (
            <div>
              <Label>İlgili Fatura (opsiyonel)</Label>
              <Select value={linkedInvoiceId || "none"} onValueChange={(v) => setLinkedInvoiceId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="tx-invoice-select">
                  <SelectValue placeholder="Fatura seçin veya boş bırakın" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Faturasız —</SelectItem>
                  {invoiceOptions.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-zinc-500">Bu cariye ait fatura yok</div>
                  )}
                  {invoiceOptions.map(inv => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} — {(inv.date || "").slice(0,10)} — {Number(inv.total).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {inv.currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-zinc-500 mt-1">Bu işlem seçili faturanın tahsilat/ödemeleri arasında görünecektir.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button data-testid="tx-save-button" onClick={save} disabled={saving || isInvoiceLocked} className="bg-zinc-900 hover:bg-zinc-800">
            {saving ? "Kaydediliyor..." : (editing ? "Güncelle" : "Kaydet")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
